import {
  Grid, Paper, Typography, Box, TextField, Button,
  InputAdornment, CircularProgress, Dialog, IconButton
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import CloseIcon from "@material-ui/icons/Close";
import { DropzoneArea } from "material-ui-dropzone";
import PageTitle from "components/PageTitle/PageTitle";
import { useEffect, useState } from "react";
import { dbGet, dbPostWithFormData } from "utils/DBFetchers";
import { SnackbarHandler } from "components/Handlers/SnackbarHandler";
import CustomizedSnackbars from "components/CustomizedSnackbars/CustomizedSnackbars";
import PopupMessage from "components/PopupMessage/PopupMessage";

import useStyles from "./styles";

export default function PharmacyCreditNotePage() {
  const classes = useStyles();
  const [balance, setBalance] = useState(0);
  const [pharmacyRut, setPharmacyRut] = useState("");
  const [orderValue, setOrderValue] = useState("");
  const [creditValue, setCreditValue] = useState("");

  const [descripcion, setDescripcion] = useState("");
  const MAX_CHARS = 200;
  const restantes = MAX_CHARS - descripcion.length;
  const [snackbarStatus, snackbarOpen, onCloseSnackbar] = SnackbarHandler();

  const [showConfirmationPopUp, setShowConfirmationPopUp] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [dzKey, setDzKey] = useState(0); // para resetear el Dropzone

  const [orderNumber, setOrderNumber] = useState("");


  // estado
  const [file, setFile] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const isPdf = file?.type === "application/pdf";

  // efecto: crear URL blob y revocar
  useEffect(() => {
    if (!file) {
      if (fileUrl) URL.revokeObjectURL(fileUrl);
      setFileUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setFileUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);


  const isCreditValueAndDescriptionFieldEnabled = Number(orderValue) > 0;

  // Derivados numéricos
  const numericBalance = Number(balance) || 0;
  const numericOrder = Number(orderValue) || 0;
  // Sugerido: mínimo entre saldo y orden de compra
  const suggestedCredit = Math.max(0, Math.min(numericBalance, numericOrder));
  // ¿El valor actual excede el sugerido?
  const creditExceedsSuggested = Number(creditValue || 0) > (suggestedCredit || 0);
  // El botón "Usar saldo" sólo si hay sugerido > 0
  const canUseSuggested = isCreditValueAndDescriptionFieldEnabled && suggestedCredit > 0;
  // Para mostrar el sugerido con 2 decimales
  const formattedSuggested = isCreditValueAndDescriptionFieldEnabled ? `$${new Intl.NumberFormat("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(suggestedCredit)
    }` : "saldo";

  const isFormComplete =
    isCreditValueAndDescriptionFieldEnabled &&
    Number(creditValue) > 0 &&
    Boolean(descripcion.trim()) &&
    !creditExceedsSuggested &&
    file instanceof File
    && Boolean(orderNumber.trim());


  const RequiredMark = () => <span style={{ color: "#c62828", marginLeft: 4 }}>*</span>;

  const onClickConfirm = () => {
    if (!isFormComplete) return;
    setShowConfirmationPopUp(true);
  };

  const WARN_REMAINING = 20; // cuando queden 20 o menos, “rojo”
  const [shakeCounter, setShakeCounter] = useState(false);

  const counterColor =
    restantes <= 0 ? "#c62828" : restantes <= WARN_REMAINING ? "#c62828" : "#6b6b6b";

  // helper para detectar si la tecla agregaría caracteres
  const willInsertChar = (e) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return false;
    if (e.key === "Backspace" || e.key === "Delete" || e.key === "Tab") return false;
    if (e.key === "ArrowLeft" || e.key === "ArrowRight" || e.key === "ArrowUp" || e.key === "ArrowDown") return false;
    return e.key.length === 1 || e.key === "Enter";
  };

  const triggerShake = () => {
    setShakeCounter(true);
    setTimeout(() => setShakeCounter(false), 180); // dura lo mismo que la animación
  };

  const normalizeValueOnBlur = (value, setValue) => {
    if (!value) { setValue("0.00"); return; }
    let v = value.replace(",", ".");
    if (v === "." || v === "") { setValue("0.00"); return; }
    const n = Number(v);
    if (!isNaN(n)) setValue(n.toFixed(2));
  };

  const onChangeValue = (e, setValue) => {
    let value = e.target.value;

    // 1. Quitar caracteres inválidos (solo números, punto o coma)
    value = value.replace(/[^0-9.,]/g, "");

    // 2. Normalizar separador: reemplazar "," por "."
    value = value.replace(",", ".");

    // 3. Asegurar máximo un punto decimal
    if ((value.match(/\./g) || []).length > 1) {
      const firstDot = value.indexOf(".");
      value =
        value.slice(0, firstDot + 1) + value.slice(firstDot + 1).replace(/\./g, "");
    }

    // 4. Limitar enteros a 15 dígitos
    const parts = value.split(".");
    if (parts[0].length > 15) {
      parts[0] = parts[0].slice(0, 15);
    }

    // 5. Limitar a 2 decimales
    if (parts[1]?.length > 2) {
      parts[1] = parts[1].slice(0, 2);
    }

    value = parts.join(parts.length > 1 ? "." : "");
    setValue(value);
  };

  const onChangeOrderValue = (e) => {
    onChangeValue(e, setOrderValue);
    setCreditValue(""); // blanquea
  };

  const onChangeOrderNumber = (e) => {
    let value = e.target.value;
    // Limitar a 30 caracteres
    if (value.length > 30) value = value.slice(0, 30);
    setOrderNumber(value);
  };

  useEffect(() => {
    dbGet("wallet/internal/balances/pharmacy_supervisor")
      .then((data) => {
        setBalance(data.balance);
        setPharmacyRut(data.pharmacy_rut);
      })
      .catch((error) => {
        setBalance(0);
        setPharmacyRut("");
      });
  }, []);


  const handleUseSuggestedCredit = () => {
    const v = Number(suggestedCredit);
    if (isNaN(v) || v <= 0) return;
    setCreditValue(v.toFixed(2));
  };

  const onConfirm = () => {
    setShowConfirmationPopUp(false);
  };

  const onReconfirm = () => {
    if (submitting) return;
    setSubmitting(true);

    const formData = new FormData();
    formData.append("order_number", orderNumber.trim());
    formData.append("order_value", Number(orderValue));
    formData.append("credit_value", Number(creditValue));
    formData.append("description", descripcion.trim());
    formData.append("order_image", file);

    dbPostWithFormData("wallet/internal/pharmacy_transaction_requests", formData)
      .then((data) => {
        setOrderValue("");
        setOrderNumber("");
        setCreditValue("");
        setDescripcion("");
        setFile(null);
        setDzKey(k => k + 1);
        snackbarOpen("success", "Solicitud registrada con éxito");
      })
      .catch((error) => {
        const error_key = error?.errors?.key?.[0] || "";
        snackbarOpen("error", error_key || "Error al registrar la solicitud");
      })
      .finally(() => {
        setSubmitting(false);
        setShowConfirmationPopUp(false);
      });
  };

  return (
    <Grid>
      <PopupMessage
        title={"¿Confirma la operación?"}
        message={
          <Typography align="center">
            Se solicitará una nota de crédito<br />
            por ${creditValue ? new Intl.NumberFormat("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }).format(creditValue) : "0.00"}
          </Typography>
        }
        open={showConfirmationPopUp}
        handleClose={onConfirm}
        executeButtonRight={
          <Button
            onClick={onReconfirm}
            variant="contained"
            color="secondary"
            className={classes.buttonSpacing}
            disabled={submitting}
            aria-busy={submitting}
            style={{ minWidth: 140 }} // evita “salto” de layout
          >
            {submitting ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <CircularProgress size={18} color="inherit" />
                Procesando...
              </span>
            ) : (
              "Confirmar"
            )}
          </Button>
        }
        contentStyle={{ maxWidth: 500 }}
        buttonDivClassName={classes.popupDivCenter}
        buttonClassName={classes.buttonSpacing}
      />
      <CustomizedSnackbars
        severity={snackbarStatus.severity}
        mssg={snackbarStatus.message}
        open={snackbarStatus.open}
        onClose={onCloseSnackbar}
      />
      <PageTitle title="Billetera virtual - Solicitud de Nota de Crédito" />
      <Paper elevation={3} className={classes.paperContainer}>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight={200}
          p={3}
        >
          {/* RUT */}
          <Typography style={{ fontWeight: 600, marginBottom: 12 }}>
            Farmacia (RUT): {pharmacyRut || "-"}
          </Typography>

          {/* Saldo con recuadro */}
          <Typography style={{ fontWeight: 700, marginBottom: 8 }}>
            Saldo actual
          </Typography>
          <Box
            component={Paper}
            elevation={2}
            px={4}
            py={2}
            style={{
              borderRadius: 12,
              backgroundColor: "#f9f9f9",
              minWidth: 200,
              textAlign: "center",
            }}
          >
            <Typography variant="h5" style={{ fontWeight: 700, color: "#2e7d32" }}>
              ${balance.toLocaleString("es-AR")}
            </Typography>
          </Box>
        </Box>
      </Paper>
      <Paper elevation={3} className={classes.paperContainer}>
        {/* <Grid container spacing={3} style={{ padding: 16, background: "#f5f5f5" }}> */}
        <Grid
          container
          spacing={3}
          style={{ padding: 16, background: "#f5f5f5" }}
          alignItems="flex-start" // <-- mantiene la izquierda arriba
        >
          {/* Columna IZQUIERDA: OC + NC + Descripción */}
          <Grid item xs={12} md={6}>
            <div style={{ maxWidth: 560 }}>

              {/* N° de orden de compra */}
              <Typography style={{ fontWeight: 700, marginBottom: 8 }}>
                N° de orden de compra <RequiredMark />
              </Typography>
              <Paper
                elevation={2}
                style={{
                  borderRadius: 12,
                  background: "#ffebee",
                  padding: "12px 16px",
                  pointerEvents: "auto",
                  opacity: 1,
                  marginBottom: 16,
                }}
              >
                <TextField
                  fullWidth
                  variant="standard"
                  placeholder="Ingrese n° de orden"
                  value={orderNumber}
                  onChange={onChangeOrderNumber}
                  InputProps={{
                    disableUnderline: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <span style={{ fontWeight: 700, fontSize: "1.2rem", color: "#c62828" }}>$</span>
                      </InputAdornment>
                    ),
                    style: { fontSize: "1.2rem", fontWeight: 700, color: "#c62828" },
                  }}
                />
              </Paper>

              {/* Monto de orden de compra */}
              <Typography style={{ fontWeight: 700, marginBottom: 8 }}>
                Monto de orden de compra <RequiredMark />
              </Typography>
              <Paper
                elevation={2}
                style={{
                  borderRadius: 12,
                  background: "#ffebee",
                  padding: "12px 16px",
                  pointerEvents: "auto",
                  opacity: 1,
                  marginBottom: 16,
                }}
              >
                <TextField
                  fullWidth
                  variant="standard"
                  placeholder="Ingrese monto"
                  value={orderValue}
                  onChange={onChangeOrderValue}
                  onBlur={() => normalizeValueOnBlur(orderValue, setOrderValue)}
                  InputProps={{
                    disableUnderline: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <span style={{ fontWeight: 700, fontSize: "1.2rem", color: "#c62828" }}>$</span>
                      </InputAdornment>
                    ),
                    style: { fontSize: "1.2rem", fontWeight: 700, color: "#c62828" },
                  }}
                />
              </Paper>

              {/* Monto de nota de crédito */}
              <Typography style={{ fontWeight: 700, marginBottom: 8 }}>
                Monto de nota de crédito <RequiredMark />
              </Typography>
              <Paper
                elevation={2}
                style={{
                  borderRadius: 12,
                  background: isCreditValueAndDescriptionFieldEnabled ? "#ffebee" : "#eee",
                  padding: "12px 16px",
                  pointerEvents: isCreditValueAndDescriptionFieldEnabled ? "auto" : "none",
                  opacity: isCreditValueAndDescriptionFieldEnabled ? 1 : 0.7,
                  marginBottom: 16,
                }}
              >
                <TextField
                  fullWidth
                  variant="standard"
                  placeholder="Ingrese monto"
                  value={creditValue}
                  onChange={(e) => onChangeValue(e, setCreditValue)}
                  onBlur={() => normalizeValueOnBlur(creditValue, setCreditValue)}
                  disabled={!isCreditValueAndDescriptionFieldEnabled}
                  InputProps={{
                    disableUnderline: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <span
                          style={{
                            fontWeight: 700,
                            fontSize: "1.2rem",
                            color: isCreditValueAndDescriptionFieldEnabled ? "#c62828" : "#9e9e9e",
                          }}
                        >
                          $
                        </span>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment
                        position="end"
                        style={{ minWidth: 140, display: "flex", justifyContent: "flex-end" }}
                      >
                        <Button
                          size="small"
                          onClick={handleUseSuggestedCredit}
                          disabled={!canUseSuggested}
                          style={{
                            fontWeight: 700,
                            textTransform: "none",
                            padding: "2px 8px",
                            visibility: canUseSuggested ? "visible" : "hidden",
                            whiteSpace: "nowrap",
                          }}
                          aria-label="Usar monto sugerido"
                          aria-hidden={canUseSuggested ? undefined : true}
                          tabIndex={canUseSuggested ? 0 : -1}
                        >
                          Usar&nbsp;
                          <span style={{ fontWeight: 700, color: "#2e7d32" }}>
                            {formattedSuggested}
                          </span>
                        </Button>
                      </InputAdornment>
                    ),
                    style: { fontSize: "1.2rem", fontWeight: 700, color: "#c62828" },
                  }}
                />

                {/* Footer: reserva alto para evitar salto */}
                <div
                  style={{
                    height: 22,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    paddingTop: 6,
                  }}
                >
                  {isCreditValueAndDescriptionFieldEnabled && creditExceedsSuggested && (
                    <Typography role="alert" aria-live="assertive" style={{ fontSize: 12, color: "#c62828" }}>
                      El monto excede el máximo permitido: {formattedSuggested}
                    </Typography>
                  )}
                </div>
              </Paper>

              {/* Descripción */}
              <Typography style={{ fontWeight: 700, marginBottom: 8 }}>
                Descripción <RequiredMark />
              </Typography>
              <Paper
                elevation={2}
                style={{
                  borderRadius: 12,
                  background: "#fff",
                  border:
                    restantes <= 20 && isCreditValueAndDescriptionFieldEnabled
                      ? "1px solid #c62828"
                      : "1px solid transparent",
                  pointerEvents: isCreditValueAndDescriptionFieldEnabled ? "auto" : "none",
                  opacity: isCreditValueAndDescriptionFieldEnabled ? 1 : 0.7,
                }}
              >
                <TextField
                  value={descripcion}
                  onChange={(e) => {
                    if (!isCreditValueAndDescriptionFieldEnabled) return;
                    let val = e.target.value;
                    if (val.length > MAX_CHARS) {
                      val = val.slice(0, MAX_CHARS);
                      triggerShake();
                    }
                    setDescripcion(val);
                  }}
                  onKeyDown={(e) => {
                    if (!isCreditValueAndDescriptionFieldEnabled) { e.preventDefault(); return; }
                    if (restantes <= 0 && willInsertChar(e)) { e.preventDefault(); triggerShake(); }
                    else if (restantes <= 20 && willInsertChar(e)) { triggerShake(); }
                  }}
                  placeholder="Detalles sobre la solicitud de la nota de crédito…"
                  fullWidth
                  multiline
                  minRows={4}
                  maxRows={7}
                  variant="outlined"
                  inputProps={{ maxLength: MAX_CHARS }}
                  InputProps={{ style: { borderRadius: 12 } }}
                  disabled={!isCreditValueAndDescriptionFieldEnabled}
                />
                <div
                  className={`${classes.counterFooter} ${shakeCounter ? classes.shake : ""}`}
                  style={{ color: restantes <= 20 ? "#c62828" : "#6b6b6b", opacity: isCreditValueAndDescriptionFieldEnabled ? 1 : 0.6 }}
                >
                  <Typography style={{ fontSize: 12 }}>
                    {restantes} caracteres restantes
                  </Typography>
                </div>
              </Paper>
            </div>
          </Grid>

          {/* Columna DERECHA: Imagen */}
          <Grid item xs={12} md={6} style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
            <div style={{ width: "100%" }}>
              <Typography style={{ fontWeight: 700, marginBottom: 8 }}>
                Imagen/PDF de orden de compra <RequiredMark />
              </Typography>

              {/* wrapper relativo SOLO para el dropzone + overlay */}
              <div style={{ position: "relative" }}>
                <DropzoneArea
                  key={dzKey}
                  acceptedFiles={["image/*", "application/pdf"]}
                  filesLimit={1}
                  maxFileSize={100 * 1024 * 1024}
                  dropzoneText="Arrastre el archivo o haga clic (máx. 100MB)"
                  showAlerts={["error"]}
                  showPreviews={false}
                  showPreviewsInDropzone={false}
                  showFileNamesInPreview={false}
                  disabled={Boolean(fileUrl)}
                  onChange={(files) => setFile(files?.[0] || null)}
                />


                {fileUrl && (
                  <>
                    {/* Contenedor del preview: captura el click */}
                    <div
                      onClick={() => setViewerOpen(true)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setViewerOpen(true); }}
                      aria-label="Abrir vista ampliada"
                      style={{
                        position: "absolute",
                        inset: 0,
                        borderRadius: 8,
                        cursor: "zoom-in",
                        background: "#fff",
                        overflow: "hidden",
                      }}
                    >
                      {isPdf ? (
                        <iframe
                          src={fileUrl}
                          title="Orden de compra (PDF)"
                          /* IMPORTANTE: el preview chico NO necesita interacción */
                          style={{
                            pointerEvents: "none",          // <-- deja que el contenedor reciba el click
                            width: "100%",
                            height: "100%",
                            border: 0,
                            display: "block",
                          }}
                        />
                      ) : (
                        <img
                          src={fileUrl}
                          alt="Orden de compra"
                          style={{
                            pointerEvents: "none",          // <-- idem
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            display: "block",
                          }}
                        />
                      )}
                    </div>

                    <IconButton
                      aria-label="Quitar archivo"
                      title="Quitar archivo"
                      onClick={() => { setFile(null); setDzKey(k => k + 1); }}
                      style={{ position: "absolute", top: 8, right: 8, background: "rgba(255,255,255,0.9)" }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </>
                )}
              </div>
            </div>
          </Grid>


          {/* CTA */}
          <Grid item xs={12}>
            <Grid container justify="flex-end">
              <Grid item>
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  style={{ fontWeight: 700, borderRadius: 8, textTransform: "none", padding: "8px 24px" }}
                  onClick={onClickConfirm}
                  disabled={!isFormComplete}
                >
                  Solicitar nota de crédito
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Dialog open={viewerOpen} onClose={() => setViewerOpen(false)} fullScreen>
          <div style={{ position: "relative", width: "100%", height: "100%", background: "#000" }}>
            {isPdf ? (
              <iframe
                src={fileUrl || ""}
                title="Vista ampliada (PDF)"
                style={{ width: "100%", height: "100%", border: 0, background: "#fff" }}
              />
            ) : (
              <img
                src={fileUrl || ""}
                alt="Vista ampliada"
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            )}

            {/* Botón cerrar arriba derecha */}
            <IconButton
              aria-label="Cerrar"
              onClick={() => setViewerOpen(false)}
              style={{ position: "absolute", top: 16, right: 16, color: "#fff", zIndex: 10 }}
            >
              <CloseIcon />
            </IconButton>

            {/* Acciones abajo derecha */}
            <div style={{ position: "absolute", bottom: 16, right: 16, zIndex: 10 }}>
              <Button
                onClick={() => { setFile(null); setDzKey(k => k + 1); setViewerOpen(false); }}
                variant="contained"
                color="secondary"
                style={{ marginRight: 8 }}
              >
                Quitar archivo
              </Button>
              <Button onClick={() => setViewerOpen(false)} variant="contained">
                Cerrar
              </Button>
            </div>
          </div>
        </Dialog>

      </Paper>


    </Grid>

  );
}
